import { IDSchema } from '@mntn/zod'
import { z } from 'zod'

// Shared
const CropCoordinatesRequiredSchema = z.object({
  cropX: z.number().int().min(0),
  cropY: z.number().int().min(0),
  cropWidth: z.number().int().min(1),
  cropHeight: z.number().int().min(1),
})

const CropCoordinatesOptionalSchema = CropCoordinatesRequiredSchema.partial()

const CropCoordinatesNullableSchema = z.object({
  cropX: z.number().int().nullable(),
  cropY: z.number().int().nullable(),
  cropWidth: z.number().int().nullable(),
  cropHeight: z.number().int().nullable(),
})

const ValidatedUrlSchema = z
  .string()
  .url({ message: 'Must be a valid image' })
  .refine(value => value.startsWith('http://') || value.startsWith('https://'), { message: 'Must be a valid image' })

// --------------------
// Input schemas (INSERT / UPDATE)
// --------------------

/** INSERT: url only, no crop or ID */
export const ImageAttachmentUrlOnlySchema = z.object({
  url: ValidatedUrlSchema,
})

/** INSERT: url + required crop coordinates */
export const ImageAttachmentWithRequiredCropSchema = CropCoordinatesRequiredSchema.extend({
  url: ValidatedUrlSchema,
})

/** INSERT: url + required crop + optional ID (new uploads have no ID) */
export const ImageAttachmentFormSchema = ImageAttachmentWithRequiredCropSchema.extend({
  imageAttachmentId: IDSchema.optional(),
})

/** UPDATE: url + optional ID + optional crop (for conditional validation when isFeatured may be false) */
export const ImageAttachmentFormSchemaOptionalCrop = CropCoordinatesOptionalSchema.extend({
  url: ValidatedUrlSchema,
  imageAttachmentId: IDSchema.optional(),
})

// --------------------
// Response schemas (GET)
// --------------------

/** RESPONSE: ID + url + nullable crop (crop may not be set) */
export const ImageAttachmentResponseSchema = CropCoordinatesNullableSchema.extend({
  url: z.string(),
  imageAttachmentId: IDSchema,
})

/** RESPONSE: ID + url + required crop (guaranteed to have crop data) */
export const ImageAttachmentWithCropResponseSchema = CropCoordinatesRequiredSchema.extend({
  imageAttachmentId: IDSchema,
  url: z.string(),
})

/** RESPONSE: original image data + pre-cropped URL */
export const ImageAttachmentResponseWithCroppedUrlSchema = z.object({
  original: ImageAttachmentResponseSchema,
  croppedUrl: z.string(),
})
